import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Check, Send, Lock, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import TermsGate from "@/components/TermsGate";
import ContentCalendarPreview from "@/components/ContentCalendarPreview";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PROCESS = [
  { n: "01", t: "Brand Audit & Content Pillars", d: "We study your business, your audience, and your competitors, then define 3–5 content pillars — the themes every post will ladder up to, so your feed feels intentional instead of random." },
  { n: "02", t: "Batch Production, Bi-Weekly", d: "Every two weeks we design your graphics, edit your short-form videos, and write every caption — delivered to you for approval before anything goes live. You always have the final say." },
  { n: "03", t: "Scheduling Across Every Channel", d: "Approved content is scheduled to Instagram, TikTok, Facebook, and LinkedIn at the times your audience is actually online. Your feed stays active even on your busiest weeks." },
  { n: "04", t: "Daily Engagement & Growth", d: "We reply to comments, engage with your community, and interact with accounts in your niche every day — the unglamorous work that the algorithm quietly rewards." },
  { n: "05", t: "Monthly Report, Plain English", d: "Once a month you get a simple report: what grew, what worked, what we're changing. No vanity metrics, no jargon — just what's happening and what's next." },
];

const TIERS = [
  {
    name: "The Foundation", price: 199, featured: false, lookup_key: "social_foundation",
    best: "For new and growing businesses building a polished online presence.",
    items: ["8 professionally designed posts per month", "2 edited short-form videos", "Monthly content calendar", "Caption writing & hashtag research", "Content scheduling", "Monthly performance report"],
  },
  {
    name: "The Growth", price: 399, featured: true, lookup_key: "social_growth",
    best: "For businesses ready to build consistency, visibility, and meaningful engagement.",
    items: ["12 professionally designed posts per month", "4 edited short-form videos", "Monthly content strategy", "Stories included", "Community engagement", "Content scheduling & publishing", "Monthly analytics review", "Actionable recommendations"],
  },
  {
    name: "The Signature", price: 699, featured: false, lookup_key: "social_signature",
    best: "For established businesses looking for a more complete, hands-on social presence.",
    items: ["20 professionally designed posts per month", "8 edited short-form videos", "Full social media management", "Stories & community management", "Direct-message management", "Customized content strategy", "Competitor & industry insights", "Detailed monthly reporting", "Ongoing strategic recommendations"],
  },
];

const TIER_OPTIONS = ["Social — The Foundation ($199/mo)", "Social — The Growth ($399/mo)", "Social — The Signature ($699/mo)", "Website — The Starter ($499)", "Website — The Signature ($799)", "Website — The Custom (from $1,200)", "Custom Website Build", "Not sure yet — advise me"];

export default function SocialPage({ inquiryOnly = false }) {
  const formRef = useRef(null);
  const { hash, search } = useLocation();
  const EMPTY_FORM = { name: "", email: "", business: "", handles: "", tier: "", budget: "", goals: "" };
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutKey, setCheckoutKey] = useState("");
  const [pendingPlan, setPendingPlan] = useState(null);
  const pendingPlanRef = useRef(null);

  const startPlan = (t) => {
    if (!user) { pendingPlanRef.current = t; setCheckoutKey(t.lookup_key); setAuthOpen(true); return; }
    pendingPlanRef.current = null;
    setCheckoutKey("");
    setPendingPlan({ lookup_key: t.lookup_key, name: `Social Media — ${t.name}`, price: t.price, billing: "monthly" });
  };

  useEffect(() => {
    if (authOpen) return;
    if (user && pendingPlanRef.current) startPlan(pendingPlanRef.current);
    else if (!user && pendingPlanRef.current) { pendingPlanRef.current = null; setCheckoutKey(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authOpen]);

  useEffect(() => {
    const tier = new URLSearchParams(search).get("tier");
    if (tier) setForm((f) => ({ ...f, tier }));
    if (hash === "#inquire") {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [hash, search]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent. We'll get back to you within one business day.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF6EF] blush-wash pt-32 pb-28" data-testid={inquiryOnly ? "inquire-page" : "social-page"}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {!inquiryOnly && (<>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="eyebrow">Social media, handled</span>
          <h1 className="f-display text-4xl sm:text-5xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">
            Your feed, growing every month — <span className="f-serif text-[#A8785A]">without you touching it.</span>
          </h1>
          <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-6">
            One simple monthly rate.
          </p>
          <p className="text-[#8C7562] text-base leading-relaxed max-w-2xl mt-4">
            We handle the strategy, content, scheduling, publishing, and engagement — while you stay in control with
            final approval on every post. Each month, you’ll receive a clear, straightforward report showing what was
            done and how it performed.
          </p>
          <p className="text-[#57473B] text-base leading-relaxed max-w-2xl mt-4">No contracts. No hidden fees. Cancel anytime.</p>
        </motion.div>

        {/* process */}
        <div className="mt-20 grid lg:grid-cols-[280px_1fr] gap-10">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <span className="f-mono text-[#A8785A] text-sm">HOW IT WORKS</span>
            <div className="w-10 h-px bg-[#E8DCCB] mt-3" />
          </motion.div>
          <div className="flex flex-col">
            {PROCESS.map((p) => (
              <motion.div key={p.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                className="grid sm:grid-cols-[70px_1fr] gap-4 py-7 border-b border-[#F3EAE0]" data-testid={`process-step-${p.n}`}>
                <span className="f-mono text-[#A8785A] text-sm">{p.n}</span>
                <div>
                  <h3 className="f-display font-medium text-lg tracking-tight">{p.t}</h3>
                  <p className="text-sm text-[#8C7562] leading-relaxed mt-2 max-w-2xl">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* pricing */}
        <div className="mt-24">
          <span className="eyebrow">Monthly plans</span>
          <h2 className="f-display text-3xl sm:text-4xl font-medium tracking-tight mt-4">Choose your level of support</h2>
          <p className="text-[#8C7562] text-sm max-w-2xl mt-4 mb-14 leading-relaxed">Thoughtful social media management designed to keep your brand consistent, intentional, and aligned.</p>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {TIERS.map((t) => (
              <motion.div key={t.name} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                className={`rounded-2xl p-8 flex flex-col relative ${t.featured ? "border-2 border-[#A8785A] bg-[#FFF3F8] md:-translate-y-4 shadow-[0_16px_48px_rgba(168,120,90,0.18)]" : "border border-[#E8DCCB] bg-[#FAF6EF]"}`}
                data-testid={`tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
                {t.featured && (
                  <span className="absolute -top-3.5 left-8 f-mono text-[10px] tracking-[0.2em] bg-[#A8785A] text-[#FAF6EF] font-bold rounded-full px-4 py-1.5">
                    MOST CHOSEN
                  </span>
                )}
                <h3 className="f-display text-xl font-medium tracking-tight">{t.name}</h3>
                <div className="flex items-baseline gap-1 mt-4 mb-2">
                  <span className="f-display text-4xl font-bold text-[#A8785A]">${t.price.toLocaleString()}</span>
                  <span className="f-mono text-xs text-[#8C7562]">/mo</span>
                </div>
                <p className="text-xs text-[#8C7562] mb-7">{t.best}</p>
                <ul className="flex flex-col gap-3 flex-1">
                  {t.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#57473B]">
                      <Check size={15} className="text-[#C48A9F] mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-2.5 mt-8">
                  <button onClick={() => startPlan(t)} disabled={checkoutKey === t.lookup_key || user?.social_plan?.lookup_key === t.lookup_key}
                    className={`${t.featured ? "btn-primary" : "btn-ghost"} justify-center`}
                    data-testid={`tier-start-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
                    {user?.social_plan?.lookup_key === t.lookup_key
                      ? <><BadgeCheck size={14} /> Your current plan</>
                      : <><Lock size={13} /> {checkoutKey === t.lookup_key ? "Opening checkout…" : `Start ${t.name} now`}</>}
                  </button>
                  <button
                    onClick={() => { setForm((f) => ({ ...f, tier: `Social — ${t.name} ($${t.price.toLocaleString()}/mo)` })); formRef.current?.scrollIntoView({ behavior: "smooth" }); }}
                    className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8C7562] hover:text-[#3A2E25] transition-colors py-1"
                    data-testid={`tier-cta-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
                    Ask a question first
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="f-mono text-[11px] text-[#B9A896] mt-8 text-center" data-testid="pricing-note">
            MONTH-TO-MONTH · SECURE STRIPE CHECKOUT · CANCEL ANYTIME · FINAL APPROVAL ON EVERY POST
          </p>
        </div>

        {/* inquiry form */}
        <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
          className="mt-24" data-testid="calendar-section">
          <p className="eyebrow mb-4">What a month looks like</p>
          <h2 className="f-display text-3xl sm:text-4xl font-medium tracking-tight">Your first month, mapped out.</h2>
          <p className="text-[#8C7562] text-sm max-w-2xl mt-4 mb-10 leading-relaxed">
            Toggle a plan to see the posting rhythm you'd receive — designed posts, edited videos, stories, and the report that closes every month.
          </p>
          <ContentCalendarPreview />
        </motion.section>
        </>)}

        <div ref={formRef} id="inquire" className={`${inquiryOnly ? "" : "mt-24"} grid lg:grid-cols-[1fr_1.2fr] gap-12 scroll-mt-28`}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="eyebrow">Say hello</span>
            <h2 className="f-display text-3xl sm:text-4xl font-medium tracking-tight mt-4 leading-[1.08]">
              Tell us about your business. We'll reply with a plan.
            </h2>
            <p className="text-sm text-[#8C7562] leading-relaxed mt-5 max-w-md">
              This form lands directly in our inbox. Expect a personal reply within one business day with next steps,
              a recommended tier, and a start date — no pressure, no automated sales funnel.
            </p>
          </motion.div>
          <motion.form onSubmit={submit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} className="glass rounded-2xl p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
            data-testid="inquiry-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Full Name *</label>
              <input className="field" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="inquiry-name" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Your Email *</label>
              <input className="field" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="inquiry-email" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Business Name</label>
              <input className="field" value={form.business} onChange={set("business")} placeholder="Your business" data-testid="inquiry-business" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Instagram / TikTok Handles</label>
              <input className="field" value={form.handles} onChange={set("handles")} placeholder="@yourhandle (if you have one yet)" data-testid="inquiry-handles" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">I'm Interested In</label>
              <select className="field" value={form.tier} onChange={set("tier")} data-testid="inquiry-tier">
                <option value="">Choose an option…</option>
                {TIER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Monthly Marketing Budget</label>
              <select className="field" value={form.budget} onChange={set("budget")} data-testid="inquiry-budget">
                <option value="">Choose a range…</option>
                {["Under $500", "$500 – $1,000", "$1,000 – $2,000", "$2,000+"].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Goals & Timeline</label>
              <textarea className="field" value={form.goals} onChange={set("goals")}
                placeholder="What do you want your socials to do for you in the next 90 days?" data-testid="inquiry-goals" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary justify-center sm:col-span-2 disabled:opacity-60"
              data-testid="inquiry-submit-btn">
              <Send size={15} /> {busy ? "Sending…" : "Send My Inquiry"}
            </button>
          </motion.form>
        </div>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <TermsGate plan={pendingPlan} onClose={() => setPendingPlan(null)} />
    </main>
  );
}
